import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  appendOptimizedImageQuery,
  toOptimizedImageSrc,
} from '@eliadmin/core/src/utils/image-optimization';
import React from 'react';

// sanitize-html ships without types in this project; use a typed require.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const sanitizeHtml: (html: string, options: any) => string = require('sanitize-html');

type HtmlRendererProps = {
  html: string;
  forcedTextSize?: string;
  className?: string;
  refDiv?: React.Ref<HTMLElement>; // Flexible ref for any HTML element
  wrapperElement?: React.ElementType; // Flexible element type
};

type EmbedProvider = {
  name: 'youtube' | 'vimeo';
  toEmbedSrc: (url: URL) => string | null;
};

const EMBED_PROVIDERS: EmbedProvider[] = [
  {
    name: 'youtube',
    toEmbedSrc: url => {
      if (!url || !url.hostname) return null;

      if (url.hostname === 'youtu.be') {
        const id = url?.pathname?.replace('/', '')?.trim();
        return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
      }

      if (url.hostname.endsWith('youtube.com')) {
        const id = url?.searchParams?.get('v');
        return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
      }

      return null;
    },
  },
  {
    name: 'vimeo',
    toEmbedSrc: url => {
      if (!url || !url.hostname) return null;

      if (url.hostname === 'vimeo.com') {
        const id = url?.pathname?.replace('/', '')?.trim();
        return id ? `https://player.vimeo.com/video/${id}` : null;
      }

      if (url.hostname === 'player.vimeo.com' && url?.pathname?.startsWith('/video/')) {
        return `https://player.vimeo.com${url?.pathname}`;
      }

      return null;
    },
  },
];

const ALLOWED_IFRAME_SRC_PREFIXES = [
  'https://www.youtube-nocookie.com/embed/',
  'https://www.youtube.com/embed/',
  'https://player.vimeo.com/video/',
];

function toEmbedFromOembedUrl(rawUrl: string): { provider: string; src: string } | null {
  if (!rawUrl || typeof rawUrl !== 'string') return null;

  try {
    const url = new URL(rawUrl);
    for (const provider of EMBED_PROVIDERS) {
      const src = provider.toEmbedSrc(url);
      if (src) return { provider: provider.name, src };
    }
    return null;
  } catch {
    return null;
  }
}

function transformOembedsToIframes(html: string): string {
  if (!html || typeof html !== 'string') return '';

  const replaceWithIframe = (_match: string, url: string) => {
    if (!url) return '';
    const embed = toEmbedFromOembedUrl(url);
    if (!embed) return '';

    return `
      <div class="ea-embed ea-embed--${embed.provider}">
        <iframe
          src="${embed.src}"
          title="${embed.provider} video"
          width="560"
          height="315"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
          referrerpolicy="strict-origin-when-cross-origin"
        ></iframe>
      </div>
    `;
  };

  const withFigures = html?.replace(
    /<figure[^>]*class="[^"]*\bmedia\b[^"]*"[^>]*>\s*<oembed[^>]*url="([^"]+)"[^>]*>\s*<\/oembed>\s*<\/figure>/gi,
    replaceWithIframe,
  );

  return (
    withFigures?.replace(/<oembed[^>]*url="([^"]+)"[^>]*>\s*<\/oembed>/gi, replaceWithIframe) ??
    ''
  );
}

function getOptimizedImageSrc(src: string, width?: string) {
  const optimizedSrc = toOptimizedImageSrc(src);
  const query = new URLSearchParams();

  if (width) {
    query.set('width', width);
  }

  return appendOptimizedImageQuery(optimizedSrc, query);
}

export function rewriteHtmlImageSources(html: string) {
  if (!html || typeof html !== 'string') return '';

  return html.replace(/<img\b[^>]*>/gi, tag => {
    const srcMatch = tag.match(/\bsrc=(["'])(.*?)\1/i);

    if (!srcMatch) return tag;

    const widthMatch = tag.match(/\bwidth=(["'])(.*?)\1/i);
    const optimizedSrc = getOptimizedImageSrc(srcMatch[2], widthMatch?.[2]);

    return tag.replace(srcMatch[0], `src=${srcMatch[1]}${optimizedSrc}${srcMatch[1]}`);
  });
}

const sanitizeConfig: any = {
  allowedTags: false,
  allowedAttributes: false,
  allowVulnerableTags: true,
  disallowedTagsMode: 'discard',
  exclusiveFilter(frame: any) {
    if (!frame || !frame.attribs) return false;
    return Object.keys(frame.attribs).some(attr => /^on/i.test(attr));
  },
  transformTags: {
    script: (_tagName: string, attribs: Record<string, unknown>) => {
      return {
        tagName: 'script',
        attribs,
      };
    },
    iframe: (_tagName: string, attribs: Record<string, unknown>) => {
      const src = attribs?.src ? String(attribs.src) : '';

      if (!src) {
        return { tagName: 'div', text: '', attribs: {} };
      }

      const allowed = ALLOWED_IFRAME_SRC_PREFIXES.some(prefix => src.startsWith(prefix));

      if (!allowed) {
        return { tagName: 'div', text: '', attribs: {} };
      }

      return {
        tagName: 'iframe',
        attribs: {
          src,
          title: attribs?.title ? String(attribs.title) : 'Embedded video',
          width: attribs?.width ? String(attribs.width) : '560',
          height: attribs?.height ? String(attribs.height) : '315',
          frameborder: '0',
          allow:
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
          allowfullscreen: 'true',
          referrerpolicy: 'strict-origin-when-cross-origin',
        },
      };
    },
    img: (_tagName: string, attribs: Record<string, unknown>) => {
      const src = attribs?.src ? String(attribs.src) : '';

      return {
        tagName: 'img',
        attribs: {
          ...Object.fromEntries(
            Object.entries(attribs).map(([key, value]) => [key, String(value ?? '')]),
          ),
          src: getOptimizedImageSrc(src, attribs?.width ? String(attribs.width) : undefined),
          loading: attribs?.loading ? String(attribs.loading) : 'lazy',
          decoding: attribs?.decoding ? String(attribs.decoding) : 'async',
        },
      };
    },
  },
};

export function prepareHtmlForRender(html: string) {
  const withEmbeds = transformOembedsToIframes(html);
  return sanitizeHtml(withEmbeds || '', sanitizeConfig);
}

export function HtmlRenderer(props: HtmlRendererProps) {
  const {
    html,
    forcedTextSize,
    className,
    refDiv,
    wrapperElement: Component = 'div', // Default to 'div' if not provided
  } = props;

  if (!props || !props.html) {
    return <Component ref={refDiv} className={cn('ea-html-renderer', className)} />;
  }

  const sanitized = prepareHtmlForRender(html);

  return (
    <Component
      ref={refDiv}
      className={cn(
        'font-sans font-normal leading-7',
        !forcedTextSize &&
          '[&_h1]:text-4xl [&_h2]:text-3xl [&_h3]:text-2xl [&_h4]:text-xl [&_h5]:text-lg [&_h6]:text-base [&_p]:text-base',
        !forcedTextSize &&
          '[&_h1]:font-bold [&_h2]:font-bold [&_h3]:font-bold [&_h4]:font-bold [&_h5]:font-bold [&_h6]:font-bold [&_p]:font-normal',
        !forcedTextSize &&
          '[&_h1]:leading-10 [&_h2]:leading-9 [&_h3]:leading-9 [&_h4]:leading-9 [&_h5]:leading-9 [&_h6]:leading-9 [&_p]:leading-7',
        !forcedTextSize && '[&_ol]:list-decimal [&_ul]:list-disc [&_ol]:pl-5 [&_ul]:pl-5',
        '[&_a]:text-accent [&_a]:underline',
        'ea-html-renderer',
        forcedTextSize,
        className,
      )}
      dangerouslySetInnerHTML={{ __html: sanitized || '' }}
    />
  );
}
